import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  AuthForgotPasswordRequest,
  AuthLoginRequest,
  AuthLogoutRequest,
  AuthMeResponse,
  AuthResetPasswordRequest,
  AuthForgotPasswordResponse,
  AuthLoginResponse,
  AuthLogoutResponse,
  AuthResetPasswordResponse,
} from "../web-api-client";
import { authClient } from "./authClient";

export interface AuthUser {
  email: string;
  userId: string;
  emailConfirmed?: boolean;
  roles: string[];
  /** Funcionalidades concedidas (união dos papéis); Administrator recebe todas. */
  features: string[];
}

interface AuthContextValue {
  isAuthenticated: boolean;
  isChecking: boolean;
  user: AuthUser | null;
  refresh: () => Promise<void>;
  login: (email: string, password: string) => Promise<AuthLoginResponse>;
  logout: () => Promise<AuthLogoutResponse>;
  forgotPassword: (
    email: string,
  ) => Promise<AuthForgotPasswordResponse>;
  resetPassword: (
    email: string,
    resetToken: string,
    newPassword: string,
  ) => Promise<AuthResetPasswordResponse>;
}

const AuthContext = createContext<AuthContextValue | undefined>(
  undefined,
);

const mapUser = (
  response: AuthMeResponse | null,
): AuthUser | null => {
  if (!response?.isAuthenticated) return null;
  return {
    email: response.email ?? "",
    userId: response.userId ?? "",
    emailConfirmed: response.emailConfirmed ?? undefined,
    roles: response.roles ?? [],
    features: response.features ?? [],
  };
};

export function AuthProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [isAuthenticated, setIsAuthenticated] =
    useState(false);
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isChecking, setIsChecking] = useState(true);

  const refresh = useCallback(async () => {
    setIsChecking(true);
    try {
      const response = await authClient.me();
      const mappedUser = mapUser(response);
      setUser(mappedUser);
      setIsAuthenticated(!!mappedUser);
    } catch {
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setIsChecking(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = useCallback(
    async (email: string, password: string) => {
      const response = await authClient.login(
        new AuthLoginRequest({ email, password }),
      );
      await refresh();
      return response;
    },
    [refresh],
  );

  const logout = useCallback(async () => {
    return authClient
      .logout(new AuthLogoutRequest({ refreshToken: "" }))
      .finally(refresh);
  }, [refresh]);

  const forgotPassword = useCallback(async (email: string) => {
    return authClient.forgotPassword(
      new AuthForgotPasswordRequest({ email }),
    );
  }, []);

  const resetPassword = useCallback(
    async (email: string, resetToken: string, newPassword: string) => {
      return authClient.resetPassword(
        new AuthResetPasswordRequest({
          email,
          resetToken,
          newPassword,
        }),
      );
    },
    [],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      isAuthenticated,
      isChecking,
      user,
      refresh,
      login,
      logout,
      forgotPassword,
      resetPassword,
    }),
    [
      isAuthenticated,
      isChecking,
      user,
      refresh,
      login,
      logout,
      forgotPassword,
      resetPassword,
    ],
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
}
